import { keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";

import { ReviewCard } from "./card";
import { ReviewCardSkeleton } from "./card-skeleton";
import { CreationDialog } from "./creation-dialog";

import { useDi } from "~/entities/di";
import { ReviewQuery } from "~/entities/review";
import { range } from "~/shared/arr";
import { Tk, useTranslation2 } from "~/shared/i18n";
import { Button } from "~/shared/ui/button";
import { PaginationControl } from "~/shared/ui/pagination-control";
import { QueryError } from "~/shared/ui/query-error";
import { Skeleton } from "~/shared/ui/skeleton";
import { Pagination } from "~/shared/zod";

function ReviewsSectionContent() {
  const {
    postproc: { tc, tsp },
  } = useTranslation2();

  const { reviewService } = useDi();

  const [pageOptions, setPageOptions] = useState({
    ...Pagination.Options.DEFAULT_PAGE_OPTIONS,
    size: 4,
  });

  const {
    data: reviews,
    isPending: isReviewsPending,
    isError: isReviewsError,
    refetch: refetchReviews,
    isPlaceholderData: isPlaceholderReviews,
  } = ReviewQuery.useGetPaginated(
    reviewService,
    {
      pagination: pageOptions,
    },
    {
      placeholderData: keepPreviousData,
      retry: false,
    },
  );

  const REVIEWS_CONTAINER_CLASS_NAME =
    "grid max-h-192 grid-cols-1 gap-4 overflow-auto sm:gap-8 md:grid-cols-2";

  return (
    <div className="flex flex-col gap-4">
      <CreationDialog
        triggerProps={{
          className: "self-end",
          children: <Button>{tc(Tk.WRITE_A_REVIEW)}</Button>,
        }}
      />
      {isReviewsError ? (
        <QueryError onTryAgain={() => void refetchReviews()} />
      ) : isReviewsPending ? (
        <>
          <div className={REVIEWS_CONTAINER_CLASS_NAME}>
            {range(pageOptions.size).map((value) => (
              <ReviewCardSkeleton key={value} />
            ))}
          </div>
          <Skeleton className="h-9 w-full" />
        </>
      ) : reviews.items.length === 0 ? (
        <p className="text-center text-2xl">{tsp(Tk.NO_REVIEWS_YET)}</p>
      ) : (
        <>
          <div className={REVIEWS_CONTAINER_CLASS_NAME}>
            {reviews.items.map(({ id, ...review }) => (
              <ReviewCard key={id} review={review} />
            ))}
          </div>
          <PaginationControl
            totalItems={reviews.meta.totalItems}
            options={pageOptions}
            onOptionsChange={setPageOptions}
            showSpinner={isPlaceholderReviews}
          />
        </>
      )}
    </div>
  );
}

export { ReviewsSectionContent };
